<template>
  <div :class="stylingClass">
    <validation-observer>
      <validation-provider
        name="confirm"
        :rules="item.required ? 'required' : ''"
        v-slot="{ errors, invalid, touched }"
      >
        <div class="password-control">
          <label>Password<span v-if="item.required">*</span></label>
          <input
            v-model="item.value"
            placeholder="your password"
            :type="showPasswordValues ? 'text' : 'password'"
            name="new_password"
            @input="validateComplexity"
            :class="[
              { invalid: invalid },
              { touched: touched },
              { 'has-errors': errors.length > 0 },
            ]"
          />
          <span class="validation-label">{{ errors[0] }}</span>
        </div>
      </validation-provider>
      <validation-provider
        :rules="
          item.required ? 'required|password:@confirm' : 'password:@confirm'
        "
        v-slot="{ errors, invalid, touched }"
      >
        <div class="password-control">
          <label>Re-enter password<span v-if="item.required">*</span></label>
          <input
            v-model="confirmation"
            placeholder="your password"
            :type="showPasswordValues ? 'text' : 'password'"
            name="confirm_password"
            :class="[
              { invalid: invalid },
              { touched: touched },
              { 'has-errors': errors.length > 0 },
            ]"
          />
          <span class="validation-label">{{ errors[0] }}</span>
        </div>
      </validation-provider>
    </validation-observer>

    <div class="display-password">
      <div
        @click="showPasswordValues = !showPasswordValues"
        class="display-password-slider"
        :class="{ 'display-password-slider--active': showPasswordValues }"
      />
      Show passwords
      <EyeIcon width="16" height="16" />
    </div>

    <div class="password-checker">
      <b>Your Password must contain:</b>
      <ul>
        <li :class="{ passed: isCorrectLength }">8 - 72 Characters</li>
        <li :class="{ passed: hasUppercaseLetter }">A uppercase letter</li>
        <li :class="{ passed: hasLowercaseLetter }">A lowercase letter</li>
        <li :class="{ passed: hasNumber }">1 Number</li>
        <li :class="{ passed: hasSpecialCharacter }">
          1 Special character
          <span style="letter-spacing: 2px">!@#$%^&amp;*&#41;&#40;+=._-</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { ref } from "@vue/composition-api";
import { ValidationObserver, ValidationProvider, extend } from "vee-validate";
import { required } from "vee-validate/dist/rules";
import EyeIcon from "../assets/icons/eye.svg";

extend("required", {
  ...required,
  message: "This field is required",
});

extend("password", {
  params: ["target"],
  validate(value, { target }) {
    return value === target;
  },
  message: "It looks like your passwords do not match",
});

extend("complexity", {
  validate(value) {
    return value.match(
      /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*)(+=._-]).{8,}$/,
    );
  },
  message:
    "It looks like your password does not match the complexity requirements. Passwords must be between 6 and 72 characters long and contain 1 uppercase letter, 1 lowercase letter, 1 number and a special character !@#$%^&*)(+=._-",
});

export default {
  props: ["item", "stylingClass"],
  components: {
    EyeIcon,
    ValidationProvider,
    ValidationObserver,
  },
  data: () => {
    return {
      confirmation: "",
    };
  },
  setup() {
    const isCorrectLength = ref(false),
      hasUppercaseLetter = ref(false),
      hasLowercaseLetter = ref(false),
      hasNumber = ref(false),
      hasSpecialCharacter = ref(false),
      showPasswordValues = ref(false);

    const byteSize = (str) => new Blob([str]).size;

    const validateComplexity = (e) => {
      const choosenPassword = e.target.value;

      isCorrectLength.value =
        choosenPassword.length >= 8 && byteSize(choosenPassword) <= 72;
      hasUppercaseLetter.value = /(?=.*[A-Z])/.test(choosenPassword);
      hasLowercaseLetter.value = /(?=.*[a-z])/.test(choosenPassword);
      hasNumber.value = /(?=.*\d)/.test(choosenPassword);
      hasSpecialCharacter.value = /(?=.*[!@#$%^&*)(+=._-])/.test(
        choosenPassword,
      );
    };

    return {
      isCorrectLength,
      hasUppercaseLetter,
      hasLowercaseLetter,
      hasNumber,
      hasSpecialCharacter,
      showPasswordValues,
      validateComplexity,
    };
  },
};
</script>

<style lang="scss" scoped>
.display-password {
  align-items: center;
  display: flex;
  font-size: 14px;
  line-height: 16px;
  margin-bottom: 16px;
  padding: 4px 0;

  &-slider {
    background-color: var(--mid-grey);
    border-radius: 50px;
    box-sizing: border-box;
    cursor: pointer;
    width: 45px;
    height: 24px;
    margin-right: 8px;
    padding: 2px 4px;
    position: relative;
    transition: all 0.4s ease-in-out;

    &.display-password-slider--active {
      background-color: var(--green);

      &:after {
        left: 4px;
        transform: translate(0, -50%);
      }
    }

    &:after {
      content: "";
      position: absolute;
      background: #ffffff;
      border-radius: 500px;
      height: 20px;
      left: calc(100% - 4px);
      top: 50%;
      transform: translate(-100%, -50%);
      transition: all 0.4s ease-in-out;
      width: 20px;
    }
  }

  img {
    margin-left: 8px;
  }
}

.password-checker {
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  font-size: 14px;
  line-height: 28px;
  max-width: 350px;
  padding: 32px;

  ul {
    list-style: none;
    margin: 4px 0 0;
    padding: 0;

    li {
      background-image: url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAARCAYAAADUryzEAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAFrSURBVHgBlVPbTcMwFI3dKvkNG2QENqB85iGFTEAZgQkCEzQbNEwQUBLlOxMAE9AR+E2UB+dUteSGFNorWb6+vufcly2ME5JlmY2Ny4iiaHfKT0wNZVmuhRDhMAw29j1wHEdHSsn9xff9dJagqioHDpu+7z+7rksQ9Vt35D3saxCFbdtGKiuhgbdYj4jwYfwhyrdpmgeSSBqRbkzDf2CK67o7BrIsa8uzzPN8RUVvFBroTIG6jYFQak0sSpL3qOlZc7TJjmZeKxt1FVHJcrlMiZXotK1HZ/NQTgT7hkAu6rRNS4HdkRzXNF2dRIGnU1Gyz2DuwjRNZ07/RTBn1NPWy5klQAlvahIUNhGAWKWtlRPrQGL46IR6GJ7n3RoXCDJ654uU7CazAFF8Lpi+xBx9MqSUnENCn6IoUnU++o0geVosFjf8dUjvVY2OfcEk7g6/tA6CIJklOERw4LSCGnLE0K+wf2HVaGY6fQ8/aGQBVDwgZPcAAAAASUVORK5CYII=");
      background-repeat: no-repeat;
      background-position: left center;
      margin-bottom: 4px;
      padding-left: 20px;

      &.passed {
        color: var(--green);
        background-image: url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAARCAYAAADUryzEAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAGSSURBVHgBlVNRTsJAEH2zrPG3N1BPoDeQnkD5M1gC3IAvk2qMJUaa+AOeAEwa4h94gpYTCCcQboCJPyRlx90iBqENMB9Ndve9N9OZN4SUeAyd/IHgC2ZY5qyYJgqI7u0gWsfSOlESmmC866MGx+PFizwGuAKiIy36cmsH/Q0Bf+A8QOFkhu+aZ/enaZX54ZUWynkQ9OmeB/WVh2LFDx0PO4bGthrhdS2pIFEl2XbzgY09wo+ccMayIACRV6xesW8w6geIawIkynd2t7MN3xiUml54aS3PM8hhjnAqdBen28h+VGqTmo9Wm+vZnSkDljCfNNIymyGDeeJmVCmRIuCFFeuQ4p4mjxfkwENG6CZiagj/BHR50I1lVqMscjI95i9pnGW6aXirAHdbY4UsK6UWjjQzXbhstzDYRuR8LH9BzzSuMsneLiK/xusRx4U/Add+GycX2pFP2tZZZLNsBpNjVTUcc0cb6pB663BOxEOlMEmyCOgtpLzZUr1srVU/UFa257B4FkMcE8jKYT68sbvDNNwPnne7IEcI9B4AAAAASUVORK5CYII=");

        > span {
          color: var(--green);
        }
      }
    }
  }
}
</style>
